import { UserCircleIcon } from '@phosphor-icons/react';
import { Breadcrumb } from '@whistler-ui/k2/components/Breadcrumb';
import { Button } from '@whistler-ui/k2/components/Button';
import { Dropdown } from '@whistler-ui/k2/components/Dropdown';
import { Popover } from '@whistler-ui/k2/components/Popover';
import { Skeleton } from '@whistler-ui/k2/components/Skeleton';
import { Stack } from '@whistler-ui/k2/components/Stack';
import { tryCatch } from '@whistler-ui/k2/helpers';
import { getUserFacingErrorMessage } from '@whistler-ui/k2/helpers/userFacingError';
import { useAppMessages } from '@whistler-ui/k2/hooks/useAppMessages';
import { ValueLabelType } from '@whistler-ui/k2/types/common';
import { EnvironmentToggle } from '@whistler-ui/portal/components/EnvironmentToggle';
import { UpgradeRequestModal } from '@whistler-ui/portal/components/UpgradeRequestModal';
import {
  useGetCarrierGroups,
  useSwitchGroupCarrier,
} from '@whistler-ui/portal/hooks/queries/useMembers';
import { useHasGroupAccess } from '@whistler-ui/portal/hooks/useHasGroupAccess';
import { useRoleKeeper } from '@whistler-ui/portal/hooks/useRoleKeeper';
import { useAuthStore } from '@whistler-ui/portal/store/auth';
import { useEntitlementsStore } from '@whistler-ui/portal/store/entitlements';
import { useThemeStore } from '@whistler-ui/portal/store/theme';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { routes } from '../../routes';
import { ProfileMenu } from '../ProfileMenu';

const componentName = 'c-Header';

export const Header = () => {
  const { t } = useTranslation();
  const [setMessage] = useAppMessages(t);
  const { colors } = useThemeStore();
  const activePath = useLocation().pathname;
  const [can] = useRoleKeeper();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const hasGroupAccess = useHasGroupAccess();

  const { isLiveView, loading } = useEntitlementsStore();

  const {
    authValues: {
      organisationState: { isValidProductionUpgrade, organisation },
    },
  } = useAuthStore();

  const {
    mutateAsync: switchGroupCarrier,
    isPending: isSwitchingGroupCarrier,
  } = useSwitchGroupCarrier();

  const {
    data: carrierGroups,
    isLoading: isLoadingCarrierGroups,
    error: carrierGroupsError,
  } = useGetCarrierGroups({ isGroupCarrier: hasGroupAccess });

  useEffect(() => {
    if (carrierGroupsError) {
      setMessage({
        type: useAppMessages.type.ERROR,
        msg: getUserFacingErrorMessage(
          carrierGroupsError,
          'common:errors.default',
        ),
      });
    }
  }, [carrierGroupsError]);

  const selectedCarrierGroup = useMemo(() => {
    if (organisation?.operator_name && carrierGroups) {
      const defaultCarrier = carrierGroups.find(
        group => group.label === organisation.operator_name,
      );

      if (!defaultCarrier) return;

      return defaultCarrier;
    }
  }, [organisation?.operator_name, carrierGroups]);

  const switchAltText = useMemo(() => {
    if (!isValidProductionUpgrade) {
      return t('header_environment_switch_disabled');
    }

    return '';
  }, [isValidProductionUpgrade, t]);

  const urlIncludesEntitlements = () => {
    return activePath.includes(routes.entitlements.url);
  };

  const renderStepper = () => {
    if (urlIncludesEntitlements() && can('access-default-actions')) {
      return (
        <Breadcrumb
          routes={[
            {
              routes: routes.entitlements,
              title: 'componentStepper_routes_use_cases',
            },
            {
              routes: routes.entitlementsImplement,
            },
            {
              routes: routes.entitlementsTest,
            },
            {
              routes: routes.entitlementsTestUseCase,
            },
          ]}
        />
      );
    }

    return null;
  };

  const handleChangeCarrierGroup = async (option: ValueLabelType) => {
    const switchCarrierId = option.value;
    const { error } = await tryCatch(async () => {
      return await switchGroupCarrier({ carrierGroupId: switchCarrierId });
    });
    if (error) {
      setMessage({
        type: useAppMessages.type.ERROR,
        msg: getUserFacingErrorMessage(error, 'common:errors.default'),
      });
      return;
    }

    // Reloading the page to reflect the change in carrier group across the portal.
    // This is necessary because the carrier group information is used in various parts of the portal
    // and a full reload ensures that all components reflect the updated carrier group.
    window.location.reload();
  };

  const handleUpgrade = () => {
    setShowUpgradeModal(true);
  };

  const renderToggler = () => {
    if (loading) {
      return <Skeleton width={80} height={30} />;
    }

    if (typeof isLiveView === 'boolean') {
      return (
        <EnvironmentToggle
          disabled={!isValidProductionUpgrade}
          name="UpgradeSwitch"
          tooltipText={switchAltText}
          isValidProductionUpgrade={isValidProductionUpgrade}
        />
      );
    }

    return null;
  };

  return (
    // `nav-clearance` sits on the <nav> rather than a wrapper so the white bar
    // and bottom border still span the viewport: the rail overlaps them from
    // above, while the row inside clears it.
    <nav className="nav-clearance sticky top-0 z-50 w-full bg-white min-h-(--header-height) border-b border-b-gray-200">
      <Stack
        direction="row"
        align="center"
        className="mx-auto w-full max-w-360 h-(--header-height)"
      >
        {renderStepper()}
        <Stack
          direction="row"
          align="center"
          gap={{ sm: 3.5, md: 6 }}
          className="ml-auto shrink-0"
        >
          {!isValidProductionUpgrade && (
            <Fragment>
              <Button
                type="button"
                id={`${componentName}__button`}
                color="secondary"
                size="xs"
                rounded
                onClick={handleUpgrade}
              >
                {t('componentHeader_button_upgrade_to_prod')}
              </Button>
              <UpgradeRequestModal
                show={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
              />
            </Fragment>
          )}

          {renderToggler()}

          {hasGroupAccess && (
            <Dropdown.Menu
              isLoading={isLoadingCarrierGroups}
              isLoadingChange={isSwitchingGroupCarrier}
              id="carrier-group"
              selected={selectedCarrierGroup}
              options={carrierGroups}
              onOptionSelect={handleChangeCarrierGroup}
              charLength={19}
              width={190}
            />
          )}

          <Popover
            id="profile-menu"
            defaultPosition="left"
            trigger={() => (
              <UserCircleIcon
                size={24}
                color={colors.colorFontPrimary}
                className={`${componentName}__profile-button`}
              />
            )}
            content={() => <ProfileMenu />}
          />
        </Stack>
      </Stack>
    </nav>
  );
};
